<template>
  <AuthLayout :back-to="{ name: 'Home' }">
    <!-- Step 1: Login -->
    <form v-if="step === 'login'" class="form-stack" @submit.prevent="attemptLogin">
      <div class="auth-form-header">
        <h1>Welcome back</h1>
        <p>Sign in to continue to your Givaroo account.</p>
      </div>

      <InputField
        v-model:value="form.email.value"
        :errors="form.email.errors"
        label-size="small"
        input-text-size="small"
        @blur="form.email.validate()"
        type="email"
        data-cy="email"
        label="Email"
        placeholder="Enter email..."
      />
      <InputField
        v-model:value="form.password.value"
        :errors="form.password.errors"
        label-size="small"
        input-text-size="small"
        @blur="form.password.validate()"
        type="password"
        data-cy="password"
        label="Password"
        placeholder="Enter password..."
      />

      <p v-if="loginError" class="text-sm text-red-600">{{ loginError }}</p>

      <BaseButton
        block
        variant="primary"
        size="md"
        data-cy="submit"
        type="submit"
        :loading="loadingLogin"
        :disabled="loadingLogin"
      >
        {{ loadingLogin ? 'Logging in...' : 'Log in' }}
      </BaseButton>

      <div class="text-center">
        <RouterLink :to="{ name: 'RequestPasswordReset' }" class="auth-shell-link">
          Forgot password?
        </RouterLink>
      </div>
    </form>

    <!-- Step 2: MFA Enroll -->
    <div v-else-if="step === 'enroll'" class="form-stack">
      <div class="auth-form-header text-center sm:text-left">
        <h1>Secure your account</h1>
        <p>
          Multi-factor authentication is required for an extra layer of protection on your Givaroo
          account.
        </p>
      </div>

      <BaseButton block variant="primary" size="md" @click="step = 'otpSetup'">Continue</BaseButton>

      <div class="text-center">
        <button type="button" class="auth-shell-link" @click="step = 'login'">Back to login</button>
      </div>
    </div>

    <!-- Step 3: OTP Setup -->
    <div v-else-if="step === 'otpSetup'" class="form-stack">
      <div class="auth-form-header text-center sm:text-left">
        <h1>Register a verification method</h1>
        <p>Select how you would like to receive your one-time password.</p>
      </div>

      <button type="button" class="auth-option-card w-full text-left" @click="sendEmailOTP(false)">
        <div class="auth-option-card__icon">
          <EnvelopeIcon class="h-5 w-5" aria-hidden="true" />
        </div>
        <div>
          <h2 class="text-sm font-semibold text-gray-900">One-time password via email</h2>
          <p class="mt-1 text-sm text-gray-600">
            Receive a verification code directly in your email inbox.
          </p>
        </div>
      </button>

      <div class="text-center">
        <button type="button" class="auth-shell-link" @click="step = 'login'">Cancel</button>
      </div>
    </div>

    <!-- Step 4: OTP Verification -->
    <form v-else-if="step === 'otpVerify'" class="form-stack" @submit.prevent="submitOTP">
      <div class="auth-form-header">
        <h1>Check your email</h1>
        <p>Enter the 6-digit verification code we sent to your inbox.</p>
      </div>

      <InputField
        v-model:value="otp"
        :errors="[]"
        label-size="small"
        input-text-size="small"
        type="text"
        maxlength="6"
        label="Verification code"
        placeholder="Enter 6-digit code"
      />

      <p v-if="otpError" class="text-sm text-red-600">{{ otpError }}</p>

      <BaseButton
        block
        variant="primary"
        size="md"
        data-cy="submit"
        type="submit"
        :loading="loadingVerify"
        :disabled="loadingVerify || otp.length < 6"
      >
        {{ loadingVerify ? 'Verifying...' : 'Verify code' }}
      </BaseButton>

      <div class="text-center">
        <button
          class="auth-shell-link disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
          type="button"
          :disabled="resendDisabled || loadingResend"
          @click="handleResendOTP"
        >
          {{
            loadingResend
              ? 'Resending...'
              : resendDisabled
              ? `Resend code in ${formattedTime}`
              : 'Resend code'
          }}
        </button>
      </div>
    </form>
  </AuthLayout>
</template>

<script>
import { ref, onUnmounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { EnvelopeIcon } from '@heroicons/vue/24/outline'
import { LoginForm, UserApi } from '@/services/users/'
import InputField from '@/components/inputs/InputField'
import AuthLayout from '@/components/auth/AuthLayout.vue'
import IdleTimer from '@/services/idleTimer/'

export default {
  name: 'Login',
  components: {
    InputField,
    AuthLayout,
    EnvelopeIcon,
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const toast = useToast()

    // Errors whose server message explains what to do. Anything else keeps the generic message, so a wrong
    // password still just says "Incorrect email or password."
    const knownLoginError = (err) => {
      const data = err?.response?.data
      if (data?.account_inactive) return data.detail // deactivated account (403)
      if (err?.response?.status === 429) {
        return data?.detail || 'Too many attempts. Please try again later.' // login/OTP lockout
      }
      if (data?.detail === 'Invalid OTP.')
        return 'That code is incorrect. Check the latest email and try again.'
      return null
    }
    const form = ref(new LoginForm())
    const loginError = ref('')
    const otpError = ref('')
    const otp = ref('')
    // Held in this page's memory only for the MFA round-trip. Never written to
    // Vuex / localStorage, and cleared when the page unmounts or login finishes.
    const pendingPassword = ref('')

    const loadingLogin = ref(false)
    const loadingVerify = ref(false)
    const loadingResend = ref(false)

    const step = ref('login')

    const resendDisabled = ref(true)
    const countdown = ref(120)
    let timer = null

    const formattedTime = computed(() => {
      const minutes = Math.floor(countdown.value / 60)
      const seconds = countdown.value % 60
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    })

    const startCountdown = () => {
      resendDisabled.value = true
      countdown.value = 120
      clearInterval(timer)
      timer = setInterval(() => {
        if (countdown.value > 0) {
          countdown.value--
        } else {
          resendDisabled.value = false
          clearInterval(timer)
        }
      }, 1000)
    }

    onUnmounted(() => {
      clearInterval(timer)
      pendingPassword.value = ''
    })

    const isFundraiser = (fundraiser) => {
      return (
        fundraiser.groups &&
        fundraiser.groups.length === 1 &&
        fundraiser.groups.some((g) => g.name === 'Fundraiser')
      )
    }

    const attemptLogin = async () => {
      loginError.value = ''
      const unwrappedForm = form.value
      unwrappedForm.validate()

      if (!unwrappedForm.isValid) return

      try {
        loadingLogin.value = true
        pendingPassword.value = unwrappedForm.password.value
        const res = await UserApi.csc.login({
          email: unwrappedForm.email.value,
          password: unwrappedForm.password.value,
          phase: 'login',
        })
        // Pure fundraisers get a token immediately (flat user payload).
        // Staff/admins get a nested ``user`` and must continue through MFA.
        if (res.user) {
          const tempUser = {
            ...res.user,
            email: unwrappedForm.email.value,
          }

          await store.dispatch('setUser', tempUser)

          if (res.user.mfaActivated) {
            await sendEmailOTP(false)
          } else {
            step.value = 'enroll'
          }
        } else {
          pendingPassword.value = ''
          await store.dispatch('setUser', res)
          if (!isFundraiser(res)) {
            IdleTimer.start()
          }
          router.push({ name: 'CampaignList' })
        }
      } catch (err) {
        if (err.response?.data?.password_expired) {
          const expiryMessage =
            err.response.data.expiry_message ||
            'Your password has expired. Please reset it to continue.'
          toast.error(expiryMessage)
          router.push({ name: 'RequestPasswordReset' })
        } else {
          loginError.value = knownLoginError(err) || 'Incorrect email or password.'
        }
      } finally {
        loadingLogin.value = false
      }
    }

    const sendEmailOTP = async (resend = false) => {
      try {
        if (resend) {
          loadingResend.value = true
        }

        const user = store.getters.user
        if (!user?.id || !user?.email || !pendingPassword.value) {
          otpError.value = 'Missing login information. Please try again.'
          step.value = 'login'
          return
        }

        await UserApi.csc.login({
          phase: 'email',
          user_id: user.id,
          email: user.email,
          password: pendingPassword.value,
        })
        if (resend == false) {
          step.value = 'otpVerify'
          startCountdown()
        }
      } catch (err) {
        console.error(err)
        loginError.value =
          knownLoginError(err) ||
          (resend
            ? 'Failed to resend OTP. Please try again.'
            : 'Failed to send OTP. Please try again.')
      } finally {
        if (resend) {
          loadingResend.value = false
        }
      }
    }

    const submitOTP = async () => {
      if (otp.value.length !== 6) {
        otpError.value = 'Please enter a 6-digit OTP.'
        return
      }

      const user = store.getters.user
      if (!user?.id || !user?.email || !pendingPassword.value) {
        otpError.value = 'Missing login information. Please try again.'
        step.value = 'login'
        return
      }

      try {
        loadingVerify.value = true
        const response = await UserApi.csc.login({
          phase: 'verify',
          user_id: user.id,
          otp: otp.value,
          email: user.email,
          password: pendingPassword.value,
        })

        pendingPassword.value = ''
        const finalUser = {
          ...response,
          email: user.email,
        }

        delete finalUser.password
        await store.dispatch('setUser', finalUser)
        router.push({ name: 'CampaignList' })
      } catch (err) {
        console.log(err)
        otpError.value = knownLoginError(err) || 'OTP verification failed. Try again.'
      } finally {
        loadingVerify.value = false
      }
    }

    const handleResendOTP = async () => {
      await sendEmailOTP(true)
      startCountdown()
    }

    return {
      form,
      otp,
      loginError,
      otpError,
      step,
      loadingLogin,
      loadingVerify,
      loadingResend,
      resendDisabled,
      formattedTime,
      attemptLogin,
      submitOTP,
      sendEmailOTP,
      handleResendOTP,
    }
  },
}
</script>
